import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import {
  AxiosInstance,
  RequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  CustomAxiosRequestConfig
} from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { loginApi } from '@/api/personal'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

// 请求队列
let requestList: any[] = []

axiosInstance.interceptors.request.use((res: CustomAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    url.includes('OverseaLocation')
      ? url
      : import.meta.env.VITE_USE_MOCK === 'true'
        ? url.replace('/mock', '')
        : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  async (error: AxiosError) => {
    console.log('err: ' + error) // for debug
    if (error.response) {
      const { status, config } = error.response
      if (status === 403) {
        ElMessage.error(error.message)
        return Promise.reject(error)
      }
      const userStore = useUserStoreWithOut()
      const origialRequest = config as CustomAxiosRequestConfig
      const refreshToken = userStore.getRefreshToken

      if (!refreshToken) {
        userStore.logout()
      }

      if (status === 401 && origialRequest && !origialRequest._isRetry) {
        origialRequest._isRetry = true
        try {
          const res = await loginApi({ grant_type: 'refresh_token', refresh_token: refreshToken })
          if (res && res.data.access_token) {
            userStore.setToken(res.data.access_token)
            userStore.setRefreshToken(res.data.refresh_token)

            origialRequest.headers[`${userStore.getTokenKey}`] = 'Bearer ' + res.data.access_token
            // 判断 data 是否为 FormData 类型
            const isFormData = origialRequest.data instanceof FormData
            if (isFormData) {
              origialRequest.headers['Content-Type'] = 'multipart/form-data'
            }
            requestList.forEach((cb: any) => {
              cb()
            })
            requestList = []
            return service.request(origialRequest)
          } else {
            userStore.logout()
          }
        } catch (err: any) {
          // 2.2 刷新失败，只回放队列的请求
          requestList.forEach((cb: any) => {
            cb()
          })
          userStore.logout()
          ElMessage.error(err.message)
          return Promise.reject(err)
        }
      } else {
        // 添加到队列，等待刷新获取到新的令牌
        return new Promise((resolve) => {
          requestList.push(() => {
            const token = userStore.getToken
            config.headers!.Authorization = `Bearer ${token}` // 让每个请求携带token
            resolve(service.request(config))
          })
        })
      }
    }
    ElMessage.error(error.message)
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }
      console.log('-----------API URL:' + config.url + '\t\t\t\t----------API PATH:' + PATH_URL)
      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
